/**
 * Roof shapes from a drawn footprint (pure, tested in templates.test.ts): the fastest way to a whole roof.
 *
 * A rectangle (any rotation; four corners in any order around it) becomes:
 * - gable: a ridge along the long side, two faces down to the long sides (eaves), rakes on the short ends;
 *   `gable-across`: the ridge across, from the middle of one long side to the other (faces down to the short sides);
 * - hip: the ridge shortened by half the width at each end, four faces with hips (a pyramid on a square);
 * - shed: one face down to the long side nearer the equator (`equatorAzimuth`);
 * - flat: one face, every side an eave, pitch 0.
 * Any other outline becomes one face (shed or flat); ridges and hips can then be drawn on it with the line tool.
 * The lines go through `skeleton.ts`, so faces, slopes and ids follow the same rules as hand-drawn ones.
 */
import type { Vec2 } from "../editor/geo.ts";
import { addLine, ringArea, type EdgeType, type FaceSettings, type Sketch } from "./skeleton.ts";

export type RoofShape = "gable" | "gable-across" | "hip" | "shed" | "flat";

const sub = (a: Vec2, b: Vec2): Vec2 => [a[0] - b[0], a[1] - b[1]];
const add = (a: Vec2, b: Vec2): Vec2 => [a[0] + b[0], a[1] + b[1]];
const scale = (a: Vec2, k: number): Vec2 => [a[0] * k, a[1] * k];
const mid = (a: Vec2, b: Vec2): Vec2 => scale(add(a, b), 0.5);
const len = (a: Vec2) => Math.hypot(a[0], a[1]);

/** The corners counter-clockwise, starting with a long side (corner 0 → 1 is long). */
export function orderRectangle(corners: Vec2[]): Vec2[] {
  let ring = [...corners];
  if (ringArea(ring) < 0) ring = ring.reverse();
  if (ring.length === 4 && len(sub(ring[1]!, ring[0]!)) < len(sub(ring[2]!, ring[1]!))) ring = [...ring.slice(1), ring[0]!];
  return ring;
}

/** Whether four corners make a rectangle (right angles within `toleranceDeg`). */
export function isRectangle(corners: Vec2[], toleranceDeg = 4): boolean {
  if (corners.length !== 4) return false;
  for (let i = 0; i < 4; i++) {
    const a = sub(corners[(i + 3) % 4]!, corners[i]!);
    const b = sub(corners[(i + 1) % 4]!, corners[i]!);
    const cos = (a[0] * b[0] + a[1] * b[1]) / (len(a) * len(b) || 1);
    if (Math.abs(cos) > Math.sin((toleranceDeg * Math.PI) / 180)) return false;
  }
  return true;
}

/** The azimuth that faces the equator (south in the northern hemisphere). */
export const equatorAzimuth = (lat: number) => (lat >= 0 ? 180 : 0);

function lines(sketch: Sketch, segments: [Vec2, Vec2, EdgeType][], defaults: FaceSettings): Sketch {
  let s = sketch;
  for (const [a, b, type] of segments) s = addLine(s, { point: a, ...snapVertex(s, a) }, { point: b, ...snapVertex(s, b) }, type, defaults);
  return s;
}

function snapVertex(s: Sketch, p: Vec2): { vertex?: string } {
  const hit = Object.entries(s.vertices).find(([, v]) => Math.hypot(v[0] - p[0], v[1] - p[1]) < 1e-3);
  return hit ? { vertex: hit[0] } : {};
}

/** The footprint's roof added to the sketch (its corners joined to existing vertices where they meet). */
export function applyShape(sketch: Sketch, corners: Vec2[], shape: RoofShape, defaults: FaceSettings, lat = 45): Sketch {
  const flat = { ...defaults, pitch: shape === "flat" ? 0 : defaults.pitch };
  if (corners.length < 3) return sketch;
  const rect = corners.length === 4 && isRectangle(corners);
  if ((shape === "gable" || shape === "gable-across" || shape === "hip") && rect) {
    let ordered = orderRectangle(corners);
    if (shape === "gable-across") ordered = [...ordered.slice(1), ordered[0]!]; // start on a short side
    const [p0, p1, p2, p3] = ordered as [Vec2, Vec2, Vec2, Vec2];
    const width = len(sub(p3, p0));
    const along = scale(sub(p1, p0), 1 / len(sub(p1, p0)));
    let r0 = mid(p0, p3);
    let r1 = mid(p1, p2);
    if (shape === "hip") {
      const inset = Math.min(width / 2, len(sub(p1, p0)) / 2);
      r0 = add(r0, scale(along, inset));
      r1 = add(r1, scale(along, -inset));
    }
    const pyramid = len(sub(r1, r0)) < 1e-3;
    const segments: [Vec2, Vec2, EdgeType][] = [[p0, p1, "eave"], [p2, p3, "eave"]];
    if (shape !== "hip") {
      segments.push([p1, r1, "rake"], [r1, p2, "rake"], [p3, r0, "rake"], [r0, p0, "rake"], [r0, r1, "ridge"]);
    } else {
      segments.push([p1, p2, "eave"], [p3, p0, "eave"], [p0, r0, "hip"], [p3, r0, "hip"], [p1, r1, "hip"], [p2, r1, "hip"]);
      if (!pyramid) segments.push([r0, r1, "ridge"]);
    }
    return lines(sketch, segments, flat);
  }
  // one face: flat (eaves all round) or shed (down to the side nearest the equator)
  let ring = [...corners];
  if (ringArea(ring) < 0) ring = ring.reverse();
  const wanted = equatorAzimuth(lat);
  let low = 0;
  let bestScore = -Infinity;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i]!;
    const b = ring[(i + 1) % ring.length]!;
    const azimuth = ((Math.atan2(b[1] - a[1], -(b[0] - a[0])) * 180) / Math.PI + 360) % 360;
    const facing = Math.cos(((azimuth - wanted) * Math.PI) / 180) * len(sub(b, a));
    if (facing > bestScore) {
      bestScore = facing;
      low = i;
    }
  }
  const segments: [Vec2, Vec2, EdgeType][] = ring.map((a, i) => [a, ring[(i + 1) % ring.length]!, shape === "flat" || i === low ? "eave" : "rake"]);
  return lines(sketch, segments, flat);
}
